import React, { createContext, useState, useEffect, useRef } from "react";
import TopContextJSON from "../context/TopContext.json";
import EngineBootstrap from "./EngineBootstrap";
import { registerEvent, triggerEvent } from "./events/bus";
import { EventsRegistry } from "./events";


export const ContextEngine = createContext();
const defaultContext = {
  user: null,
  view: { selectedProject: "project_1", selectedSection: "section_1",topControl:100, mode: "audit"},
  panelVisibility: { left: true, right: false, center: true, bottom: false, bottomMaximized: false},
  context_units: [],
  controls: [],
  events: [], // optional list of known events  
  filters: [], // ✅ initialize as empty array
  sortConfig: {
      tableId: null,    // Can store multiple tables if needed
      columnIndex: null,
      direction: null,
  },
  modal: null,
};


export const ContextProvider = ({ children }) => {

  const awayTimerRef = useRef(null);
  const getInitialState = () => {
    const saved = localStorage.getItem("user_session");
    const initialUser = saved ? JSON.parse(saved) : null;
    

    // 2. Build the base state
    const base = { 
      ...defaultContext, 
      ...TopContextJSON,
    };
    
    // 🚀 EXPLICITLY set the user AFTER the spread so nothing can overwrite it
    base.user = initialUser || TopContextJSON.user || defaultContext.user;
      // 3. 🚀 DYNAMIC PROJECT REPLACEMENT
  // If we have a logged-in user with a projects list, replace sandbox projects
  if (initialUser?.projects && Array.isArray(initialUser.projects)) {
    
    // Remove existing "sandbox" project units from TopContextJSON
    const nonProjectUnits = base.context_units.filter(unit => unit.type !== "project");
        // Remove existing "sandbox" project units from TopContextJSON
    const SectionUnits = base.context_units.filter(unit => unit.type == "section");


    // Map your user.projects list into the "context_unit" format the engine expects
    const realProjectUnits = initialUser.projects.map((proj, index) => ({
      id: proj.cont_id || `proj_${index}`,
      type: "project",
      label: proj.cont_value || proj.cont_value || "Unnamed Project",
      status: proj.cont_status || "active", // 🚀 Keep "active" if Django says so
      metadata: { ...proj } // Store the raw Django object here just in case
    }));

    // Reconstruct the context_units with REAL projects at the top
    base.context_units = [...realProjectUnits, ...nonProjectUnits];
    
    // Update the view to point to the first real project instead of 'project_1'
    if (realProjectUnits.length > 0) {
      base.view.selectedProject = realProjectUnits[0].id;
    }
    if (SectionUnits.length > 0) {
      base.view.selectedSection = SectionUnits[0].id;
    }
  }

      if (!base.context_units?.some(u => u.type?.toLowerCase() === "table")) {
        base.context_units.unshift({
          id: "defaultTable",
          type: "table",
          cvl: [],
          data: [{ 
            row_id:null, // Wrap your key-value pairs in curly braces
            row: {},
            meta: {},
          }],
        });
      };
          // Merge panelVisibility safely
    base.panelVisibility = {
      ...defaultContext.panelVisibility,
      ...(TopContextJSON.panelVisibility || {}),
    };

    // Merge controls: keep default empty array if none
    base.controls = [
      ...(defaultContext.controls || []),
      ...(TopContextJSON.controls || []),
    ];

    // Merge events: keep default empty array if none
    base.events = [
      ...(defaultContext.events || []),
      ...(TopContextJSON.events || []),
    ];
      return base;
  };
  const [context, setContext] = useState(getInitialState);
  const socketRef = useRef(null);
  const parseNotice = (msgData) => {
    // If msgData is an object (from history), extract info. 
    // If it's a string (live message), use it directly.
    const rawMsg = typeof msgData === 'object' ? msgData.text : msgData;
    const rawTime = typeof msgData === 'object' ? msgData.created_at : null;

    if (!rawMsg) return null;

    const parts = rawMsg.split('|');
    const [kind, sender, text] = parts.length === 3 ? parts : ['SYSTEM', 'System', rawMsg];

    return {
      id: rawTime ? new Date(rawTime).getTime() + Math.random() : Date.now() + Math.random(),
      kind,
      sender,
      text,
      // 🚀 Use the DB time if available, otherwise use local time
      time: rawTime 
        ? new Date(rawTime).toLocaleTimeString() 
        : new Date().toLocaleTimeString(),
      isTransient: kind === "STATUS"
    };
  };

  useEffect(() => {
    if (!context.user?.token || !context.view?.selectedProject) return;

    const tenantId = context.user.tenant_id;
    const projectId = context.view.selectedProject;
    const wsUrl = `wss://dashboard.bimzereg.com/ws/presence/${tenantId}/${projectId}/?token=${context.user.token}`;

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket; // 🚀 Store it in the ref

    // 2. Update the useEffect logic
    const handleVisibilityChange = () => {
      if (socket.readyState !== WebSocket.OPEN) return;

      if (document.hidden) {
        // 🚀 Start a 5-minute timer before telling everyone you are "Away"
        console.log("⏳ Starting 5-minute 'Away' countdown...");
        awayTimerRef.current = setTimeout(() => {
          socket.send(JSON.stringify({ 
            type: "presence", 
            status: "Away" 
          }));
        }, 5 * 60 * 1000); // 5 minutes
      } else {
        // 🚀 If user comes back before 5 minutes, cancel the "Away" message
        if (awayTimerRef.current) {
          console.log("🛑 Cancelled 'Away' message - user returned early.");
          clearTimeout(awayTimerRef.current);
          awayTimerRef.current = null;
        } else {
          // Only send "Active" if the 5 minutes actually passed and "Away" was sent
          socket.send(JSON.stringify({ 
            type: "presence", 
            status: "Active" 
          }));
        }
      }
    };


    document.addEventListener("visibilitychange", handleVisibilityChange);

    socket.onopen = () => console.log("✅ Engine Linked to Presence");
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // 🚀 1. Handle History Batch (The 24-hour replay)
      if (data.type === 'history_batch') {
        const historyNotices = data.messages
          .map(msg => parseNotice(msg))
          .filter(n => n !== null); // Remove any broken ones

        setContext(prev => ({
          ...prev,
          view: { 
            ...prev.view, 
            notices: historyNotices.slice(0, 20) // Keep it lightweight
          }
        }));
        return;
      }
      console.log('📥 DATA RECEIVED FROM SOCKET:', data.msg); // 🚀 MOVE THIS HERE
  
      if (!data.msg || data.msg.toLowerCase().includes("unknown")) return;      

      const parts = data.msg.split('|');
      if (parts.length < 3) return;

      const [kind, sender, text] = parts;
      
      const newNotice = {
        id: Date.now() + Math.random(),
        kind,
        sender,
        text,
        time: new Date().toLocaleTimeString(),
        isTransient: kind === "STATUS" 
      };
      triggerEvent("WS_MESSAGE_RECEIVED", newNotice);

      setContext(prev => {
        const history = prev.view?.notices || [];
        const lastEntry = history[0]; // Get the most recent item

        // 🚀 LOGIC 1: OVERWRITE (Only for consecutive STATUS updates from same user)
        if (
          newNotice.kind === "STATUS" && 
          lastEntry?.kind === "STATUS" && 
          lastEntry.sender === sender
        ) {
          const updatedHistory = [...history];
          updatedHistory[0] = newNotice; // Replace only the top item
          return { 
            ...prev, 
            view: { ...prev.view, notices: updatedHistory, activeToast: null } 
          };
        }

        // 🚀 LOGIC 2: DUPLICATE FILTER (Ignore if same message sent twice)
        if (lastEntry && lastEntry.text === newNotice.text && lastEntry.kind === newNotice.kind) {
          return prev;
        }

        // 🚀 LOGIC 3: NEW LINE (For ACTIONS, CHAT, and SYSTEM)
        return {
          ...prev,
          view: {
            ...prev.view,
            notices: [newNotice, ...history].slice(0, 20),
            activeToast: newNotice.kind === "STATUS" ? null : newNotice
          }
        };
      });

      // ⏳ Auto-clear toast for non-transient messages
      if (!newNotice.isTransient) {
        setTimeout(() => {
          setContext(prev => {
            if (prev.view?.activeToast?.id === newNotice.id) {
              return { ...prev, view: { ...prev.view, activeToast: null } };
            }
            return prev;
          });
        }, 5000);
      }
    };
    // 🚀 THE CLEANUP (Essential for preventing memory leaks)
    return () => {
      console.log("🔌 Cleaning up old project room...");
      document.removeEventListener("visibilitychange", handleVisibilityChange);      
      socket.close();
      socketRef.current = null;
      
      // Optional: Clear toast when leaving the room
      setContext(prev => ({ 
        ...prev, 
        view: { ...prev.view, activeToast: null } 
      }));
    };
  }, [context.user?.token, context.view?.selectedProject]);
console.log(context.user);
const updateContext = (path, value) => {
  setContext(prev => {
    // History snapshot
    undoStack.current.push(structuredClone(prev));
    redoStack.current = [];

    const keys = path.split(".");

    const setter = (obj, pathKeys, val) => {
      const [currentKey, ...remainingKeys] = pathKeys;

      // If we are at the end of the path
      if (remainingKeys.length === 0) {
        // If it's an array and we are targeting an index (e.g. context_units.0)
        if (Array.isArray(obj) && !isNaN(currentKey)) {
          const newArr = [...obj];
          newArr[parseInt(currentKey)] = val;
          return newArr;
        }
        // Otherwise standard object update
        return { ...obj, [currentKey]: val };
      }

      // If the current level is an array
      if (Array.isArray(obj) && !isNaN(currentKey)) {
        const index = parseInt(currentKey);
        const newArr = [...obj];
        newArr[index] = setter(obj[index] || {}, remainingKeys, val);
        return newArr;
      }

      // Standard nested object descent
      return {
        ...obj,
        [currentKey]: setter(obj[currentKey] || {}, remainingKeys, val)
      };
    };

    return setter(prev, keys, value);
  });
};
const loginUser = (user) => {
  // Update "user" instead of "meta.user" to match your App.js check
  updateContext("user", user); 
};

  const undoStack = useRef([]);
  const redoStack = useRef([]);

  const contextRef = React.useRef(context);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  const getContext = () => contextRef.current;

  const undo = () => {
    if (!undoStack.current.length) return;
    redoStack.current.push(structuredClone(context));
    const previous = undoStack.current.pop();
    setContext(previous);
    contextRef.current = previous;
    triggerEvent(EventsRegistry.CONTEXT_UPDATED, { context: previous });
  };

  const redo = () => {
    if (!redoStack.current.length) return;
    undoStack.current.push(structuredClone(context));
    const next = redoStack.current.pop();
    setContext(next);
    contextRef.current = next;
    triggerEvent(EventsRegistry.CONTEXT_UPDATED, { context: next });
  };

  const handleSort = (tableId, columnIndex, direction) => {
    updateContext('view.sortConfig', { tableId, columnIndex, direction });
  };


  return (
    <ContextEngine.Provider value={{ context, getContext,updateContext,undo,redo,loginUser }}>
      <EngineBootstrap />
      {children}
    </ContextEngine.Provider>
  );
};
